import React, { useState } from 'react';
import { useRouteError, isRouteErrorResponse, useNavigate, useLocation, Link } from 'react-router-dom';
import { LogIn, RotateCcw, Copy, Check, Bug, ChevronDown, ChevronUp, Mail } from 'lucide-react';
import {
  NotFoundError,
  ForbiddenError,
  UnauthorizedError,
  ServerError,
  GenericError,
} from './components';

export interface ErrorPageProps {
  status?: number;
  code?: number;
  message?: string;
  error?: unknown;
}

export const ErrorPage: React.FC<ErrorPageProps> = ({
  status: propStatus,
  code: propCode,
  message: propMessage,
  error: propError,
}) => {
  const routeError = useRouteError();
  const navigate = useNavigate();
  const location = useLocation();
  const [copied, setCopied] = useState(false);
  const [showDevDetails, setShowDevDetails] = useState(false);

  const activeError = propError || routeError;

  // Determine HTTP status code
  let status = propStatus || propCode;
  let detailMessage = propMessage || '';
  let errorStack = '';

  if (isRouteErrorResponse(routeError)) {
    status = status || routeError.status;
    if (typeof routeError.data === 'string') {
      detailMessage = detailMessage || routeError.data;
    } else if (routeError.data && typeof routeError.data === 'object' && 'message' in routeError.data) {
      detailMessage = detailMessage || String((routeError.data as { message: unknown }).message);
    }
  } else if (activeError instanceof Error) {
    status = status || 500;
    detailMessage = detailMessage || activeError.message;
    errorStack = activeError.stack || '';
  } else if (typeof activeError === 'string') {
    status = status || 500;
    detailMessage = detailMessage || activeError;
  } else if (activeError && typeof activeError === 'object') {
    status = status || 500;
    detailMessage = detailMessage || JSON.stringify(activeError);
  }

  // Default to 404 if no status detected
  if (!status) {
    status = 404;
  }

  // Render specific child error component with custom SVG based on code
  const renderErrorContent = () => {
    switch (status) {
      case 404:
        return <NotFoundError message={detailMessage} />;
      case 403:
        return <ForbiddenError message={detailMessage} />;
      case 401:
        return <UnauthorizedError message={detailMessage} />;
      case 500:
      case 502:
      case 503:
      case 504:
        return <ServerError status={status} message={detailMessage} />;
      default:
        return <GenericError message={detailMessage} />;
    }
  };

  const handleCopyDiagnostics = () => {
    const errorText = `[Error ${status}]\nURL: ${window.location.href}\nDetails: ${detailMessage}\n${
      errorStack ? `Stack:\n${errorStack}` : ''
    }`;
    navigator.clipboard.writeText(errorText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const isDev = import.meta.env.DEV;

  return (
    <div className="min-h-screen w-full flex flex-col justify-between bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 transition-colors duration-300 font-sans selection:bg-blue-500/20">
      {/* ─── Top Header Bar ────────────────────────────────────────────── */}
      <header className="w-full max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
        {/* Top-Left Official Brand Logo (Theme-Aware) */}
        <Link to="/login" className="flex items-center transition-opacity hover:opacity-90">
          <img src="/brand/light-logo-with-text.svg" alt="SmartSite" className="h-8 sm:h-9 w-auto dark:hidden" />
          <img src="/brand/dark-logo-with-text.svg" alt="SmartSite" className="h-8 sm:h-9 w-auto hidden dark:block" />
        </Link>

        {/* Top-Right Log In Action */}
        <Link
          to="/login"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#0066b2] hover:bg-[#005290] text-white text-xs font-bold uppercase tracking-wider rounded-lg shadow-sm transition-all duration-200 hover:scale-105 active:scale-95"
        >
          <LogIn className="w-3.5 h-3.5" />
          Log In
        </Link>
      </header>

      {/* ─── Center Content Area with Dynamic SVG Error Illustration ─────── */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 py-8 my-auto">
        <div className="w-full max-w-2xl text-center space-y-8">
          {/* Specific Error Component & SVG Illustration */}
          {renderErrorContent()}

          {/* Action Buttons (Clean & Non-Duplicate) */}
          <div className="flex items-center justify-center gap-5 pt-2">
            <button
              onClick={() => {
                if (window.history.length > 1) {
                  navigate(-1);
                } else {
                  navigate('/login');
                }
              }}
              className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white font-semibold text-sm transition-colors lowercase cursor-pointer"
            >
              back
            </button>

            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-4.5 py-2 bg-slate-200/80 hover:bg-slate-300 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-semibold text-xs uppercase tracking-wider rounded-lg border border-slate-300/80 dark:border-slate-700/80 transition-all duration-200 hover:scale-[1.02] active:scale-[0.98] cursor-pointer"
              title="Reload Page"
            >
              <RotateCcw className="w-3.5 h-3.5 text-slate-500 dark:text-slate-400" />
              Reload Page
            </button>
          </div>

          {/* Support Contact Information Section */}
          <div className="pt-6 border-t border-slate-200 dark:border-slate-800/80 max-w-md mx-auto">
            <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed flex flex-col sm:flex-row items-center justify-center gap-1.5">
              <span>If you need assistance, please reach out to:</span>
              <a
                href="mailto:support@nesterra.net?subject=Support%20Request"
                className="inline-flex items-center gap-1.5 font-semibold text-[#0066b2] dark:text-blue-400 hover:underline"
              >
                <Mail className="w-3.5 h-3.5" />
                support@nesterra.net
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* ─── Footer & Developer Diagnostics (Dev Mode Only) ─────────────── */}
      {Boolean(isDev && activeError) && (
        <footer className="w-full max-w-7xl mx-auto px-6 py-6 text-center text-xs text-slate-400 dark:text-slate-600">
          <div className="max-w-xl mx-auto text-left">
            <button
              onClick={() => setShowDevDetails(!showDevDetails)}
              className="w-full flex items-center justify-between text-xs font-mono text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 py-1.5 px-3 rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-900 transition-colors"
            >
              <span className="flex items-center gap-2 font-semibold">
                <Bug className="w-3.5 h-3.5 text-amber-500" />
                Developer Diagnostics (Dev Only)
              </span>
              {showDevDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showDevDetails && (
              <div className="mt-2 space-y-2 bg-slate-900 text-slate-200 p-4 rounded-xl text-xs font-mono border border-slate-800 shadow-inner">
                <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                  <span className="text-slate-400">Path: {location.pathname}</span>
                  <button
                    onClick={handleCopyDiagnostics}
                    className="inline-flex items-center gap-1 text-slate-400 hover:text-white transition-colors"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-green-400" />
                        <span className="text-green-400">Copied</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        <span>Copy Details</span>
                      </>
                    )}
                  </button>
                </div>

                {detailMessage && (
                  <div>
                    <div className="text-slate-400 mb-1 font-sans text-[10px] uppercase tracking-wider">
                      Error Message:
                    </div>
                    <div className="text-rose-400 font-semibold break-words">{detailMessage}</div>
                  </div>
                )}

                {errorStack && (
                  <div>
                    <div className="text-slate-400 mb-1 font-sans text-[10px] uppercase tracking-wider">
                      Stack Trace:
                    </div>
                    <pre className="overflow-x-auto max-h-40 p-2 bg-slate-950 rounded border border-slate-800 text-[11px] text-slate-300 leading-relaxed">
                      {errorStack}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </footer>
      )}
    </div>
  );
};

export default ErrorPage;
